import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { playSound } from '../utils/sound';
import api from '../utils/api';
import OrderTaker from './OrderTaker';
import KitchenDisplay from './KitchenDisplay';
import AdminPanel from './AdminPanel';
import CustomerBoard from './CustomerBoard';
import { LogOut, Leaf, UtensilsCrossed, Flame, Settings, Monitor, Wifi, WifiOff, Volume2, VolumeX, UsersRound, Clock3, ShieldCheck, RefreshCw, ScrollText, Filter } from 'lucide-react';

const VIEWS = [
  { id: 'orders', label: 'Order Taker', icon: UtensilsCrossed, component: OrderTaker },
  { id: 'kitchen', label: 'Kitchen', icon: Flame, component: KitchenDisplay },
  { id: 'admin', label: 'Admin', icon: Settings, component: AdminPanel },
  { id: 'board', label: 'Customer Board', icon: Monitor, component: CustomerBoard },
];

const SUPER_ADMIN_ONLY_VIEWS = [
  { id: 'sessions', label: 'Sessions', icon: UsersRound, component: SuperAdminSessions },
  { id: 'audit', label: 'Audit Log', icon: ScrollText, component: SuperAdminAuditLog },
];

export default function SuperAdminView() {
  const { user, logout } = useAuth();
  const [activeView, setActiveView] = useState('admin');
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem('wc_sound') !== 'off');
  const availableViews = user.role === 'super_admin' ? [...VIEWS, ...SUPER_ADMIN_ONLY_VIEWS] : VIEWS;
  const activeMeta = availableViews.find(v => v.id === activeView) || availableViews[2];
  const ActiveComponent = activeMeta?.component || AdminPanel;

  const toggleSound = () => { const next = !soundEnabled; setSoundEnabled(next); localStorage.setItem('wc_sound', next ? 'on' : 'off'); };
  const onWsMessage = useCallback(() => {}, []);
  const wsConnected = useWebSocket(onWsMessage);

  useEffect(() => {
    if (!user) return;
    api.post('/auth/session/activity', { current_view: `Super Admin: ${activeMeta.label}` }).catch(() => {});
  }, [activeMeta.label, user]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="super-admin-view">
      <div className="bg-[#0D1F0D] text-white flex items-center" data-testid="super-admin-nav">
        <div className="flex items-center gap-2 px-3 py-2 shrink-0 border-r border-white/10">
          <Leaf size={16} className="text-[#7D8B69]" />
          <span className="font-mono text-xs font-bold uppercase tracking-wider text-[#7D8B69]">{user.label}</span>
        </div>
        <div className="flex flex-1 overflow-x-auto">
          {availableViews.map(v => (
            <button key={v.id} onClick={() => setActiveView(v.id)}
              data-testid={`sa-tab-${v.id}`}
              className={`flex items-center gap-1.5 px-4 py-2.5 font-mono text-xs font-bold uppercase tracking-wider transition-colors whitespace-nowrap border-r border-white/10 ${
                activeView === v.id ? 'bg-[#4A7C3F] text-white' : 'hover:bg-white/10 text-white/60'
              }`}>
              <v.icon size={14} /> {v.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 px-3 shrink-0">
          <button onClick={toggleSound} className="p-1.5 text-white/60 hover:text-white" data-testid="sa-sound-toggle">
            {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
          </button>
          <span className={`flex items-center gap-1 font-mono text-[10px] px-2 py-0.5 ${wsConnected ? 'text-[#4A7C3F]' : 'text-[#FF3333] animate-pulse'}`} data-testid="sa-connection-status">
            {wsConnected ? <><Wifi size={10} /> LIVE</> : <><WifiOff size={10} /> OFFLINE</>}
          </span>
          <button onClick={logout} className="p-1.5 hover:bg-white/10 transition-colors" data-testid="sa-logout-btn">
            <LogOut size={14} className="text-white/60 hover:text-white" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <ActiveComponent />
      </div>
    </div>
  );
}

function SuperAdminSessions() {
  const [sessions, setSessions] = useState([]);
  const [windowMinutes, setWindowMinutes] = useState(15);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchSessions = useCallback(async () => {
    try {
      setError('');
      const { data } = await api.get('/admin/sessions');
      setSessions(data.sessions || []);
      setWindowMinutes(data.active_window_minutes || 15);
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to load active sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    const interval = setInterval(fetchSessions, 30000);
    return () => clearInterval(interval);
  }, [fetchSessions]);

  return (
    <div className="min-h-full bg-[#F9F8F6] p-4 sm:p-6" data-testid="super-admin-sessions-view">
      <section className="relative overflow-hidden rounded-3xl bg-[#1B3A1A] p-6 text-white shadow-[0_18px_45px_rgba(27,58,26,0.18)]" data-testid="sessions-overview-panel">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-[#B8860B]/20 to-transparent" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#F3F0E6]" data-testid="sessions-overview-kicker"><ShieldCheck size={12} /> Hidden Owner View</div>
            <h1 className="font-heading text-3xl font-bold leading-tight sm:text-4xl">Who’s logged in right now.</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-[#F3F0E6]/85">Active devices are shown when they have checked in within the last {windowMinutes} minutes.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm" data-testid="sessions-active-count"><div className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#D4CFC5]">Active</div><div className="mt-1 font-mono text-3xl font-bold text-white">{sessions.length}</div></div>
            <button onClick={fetchSessions} className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white transition-colors duration-200 hover:bg-white/20" data-testid="sessions-refresh-btn" aria-label="Refresh active sessions"><RefreshCw size={18} /></button>
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-3xl border border-[#7D8B69]/15 bg-white p-5 shadow-[0_12px_35px_rgba(27,58,26,0.06)]" data-testid="sessions-list-panel">
        {loading && <div className="py-10 text-center font-mono text-xs uppercase tracking-wider text-[#7D8B69]" data-testid="sessions-loading">Loading sessions…</div>}
        {error && <div className="rounded-2xl bg-[#FFF3F3] p-4 font-mono text-xs font-bold uppercase tracking-wider text-[#C43030]" data-testid="sessions-error">{error}</div>}
        {!loading && !error && sessions.length === 0 && <div className="rounded-2xl border border-dashed border-[#7D8B69]/25 bg-[#FDFBF7] py-10 text-center font-mono text-xs uppercase tracking-wider text-[#7D8B69]" data-testid="sessions-empty">No active sessions in the last {windowMinutes} minutes</div>}
        {!loading && !error && sessions.length > 0 && <div className="space-y-3" data-testid="sessions-list">{sessions.map(session => <SessionCard key={session.id} session={session} />)}</div>}
      </section>
    </div>
  );
}

function SessionCard({ session }) {
  const lastActivity = formatSessionTime(session.last_activity);
  const loginTime = formatSessionTime(session.login_at);
  return (
    <article className="grid grid-cols-1 gap-4 rounded-2xl border border-[#EFECE6] bg-[#FDFBF7] p-4 transition-colors duration-200 hover:bg-[#F3F0E6] md:grid-cols-[1fr_auto] md:items-center" data-testid={`session-card-${session.id}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-heading text-lg font-bold text-[#1B3A1A]" data-testid={`session-label-${session.id}`}>{session.label}</h3>
          <span className="rounded-full bg-[#E8F0E4] px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#1B3A1A]" data-testid={`session-role-${session.id}`}>{session.role?.replace('_', ' ')}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <SessionChip label="View" value={session.current_view || 'Active'} testId={`session-view-${session.id}`} />
          <SessionChip label="Device" value={`${session.device_type || 'Device'} · ${session.browser || 'Browser'}`} testId={`session-device-${session.id}`} />
          <SessionChip label="IP" value={session.ip_address || 'Unknown'} testId={`session-ip-${session.id}`} />
          <SessionChip label="Login" value={loginTime} testId={`session-login-${session.id}`} />
        </div>
      </div>
      <div className="rounded-2xl bg-white px-4 py-3 font-mono text-xs font-bold uppercase tracking-wider text-[#7D8B69]" data-testid={`session-last-active-${session.id}`}><Clock3 size={13} className="mb-0.5 mr-1 inline" />Last {lastActivity}</div>
    </article>
  );
}

function SessionChip({ label, value, testId }) {
  return <span className="rounded-full bg-white px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#7D8B69]" data-testid={testId}>{label}: <span className="text-[#1B3A1A]">{value}</span></span>;
}

function formatSessionTime(value) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function SuperAdminAuditLog() {
  const [logs, setLogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('all');
  const [retentionDays, setRetentionDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async () => {
    try {
      setError('');
      const { data } = await api.get('/admin/audit-logs', { params: { category, limit: 150 } });
      setLogs(data.logs || []);
      setCategories(data.categories || []);
      setRetentionDays(data.retention_days || 30);
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to load audit log');
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <div className="min-h-full bg-[#F9F8F6] p-4 sm:p-6" data-testid="super-admin-audit-view">
      <section className="relative overflow-hidden rounded-3xl bg-[#1B3A1A] p-6 text-white shadow-[0_18px_45px_rgba(27,58,26,0.18)]" data-testid="audit-overview-panel">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-[#B8860B]/20 to-transparent" />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#F3F0E6]" data-testid="audit-overview-kicker"><ScrollText size={12} /> Owner Audit Log</div>
            <h1 className="font-heading text-3xl font-bold leading-tight sm:text-4xl">Actions and changes, with context.</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-[#F3F0E6]/85">Logs retain IP address, actor, device, route, status, and before/after snapshots where available for {retentionDays} days.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm" data-testid="audit-log-count"><div className="font-mono text-[10px] font-bold uppercase tracking-wider text-[#D4CFC5]">Entries</div><div className="mt-1 font-mono text-3xl font-bold text-white">{logs.length}</div></div>
            <button onClick={fetchLogs} className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-white transition-colors duration-200 hover:bg-white/20" data-testid="audit-refresh-btn" aria-label="Refresh audit log"><RefreshCw size={18} /></button>
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-3xl border border-[#7D8B69]/15 bg-white p-5 shadow-[0_12px_35px_rgba(27,58,26,0.06)]" data-testid="audit-list-panel">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#7D8B69]"><Filter size={13} /> Filter</div>
          <select value={category} onChange={e => setCategory(e.target.value)} className="h-11 rounded-xl border border-[#7D8B69]/25 bg-[#FDFBF7] px-3 font-mono text-xs font-bold uppercase tracking-wider text-[#1B3A1A]" data-testid="audit-category-filter">
            <option value="all">All categories</option>
            {categories.map(cat => <option key={cat} value={cat}>{cat.replace('_', ' ')}</option>)}
          </select>
        </div>
        {loading && <div className="py-10 text-center font-mono text-xs uppercase tracking-wider text-[#7D8B69]" data-testid="audit-loading">Loading audit log…</div>}
        {error && <div className="rounded-2xl bg-[#FFF3F3] p-4 font-mono text-xs font-bold uppercase tracking-wider text-[#C43030]" data-testid="audit-error">{error}</div>}
        {!loading && !error && logs.length === 0 && <div className="rounded-2xl border border-dashed border-[#7D8B69]/25 bg-[#FDFBF7] py-10 text-center font-mono text-xs uppercase tracking-wider text-[#7D8B69]" data-testid="audit-empty">No audit entries found</div>}
        {!loading && !error && logs.length > 0 && <div className="space-y-3" data-testid="audit-list">{logs.map(log => <AuditLogCard key={log.id} log={log} />)}</div>}
      </section>
    </div>
  );
}

function AuditLogCard({ log }) {
  const actor = log.actor || {};
  return (
    <article className="rounded-2xl border border-[#EFECE6] bg-[#FDFBF7] p-4 transition-colors duration-200 hover:bg-[#F3F0E6]" data-testid={`audit-log-${log.id}`}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-[#E8F0E4] px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#1B3A1A]" data-testid={`audit-category-${log.id}`}>{log.category?.replace('_', ' ') || 'system'}</span>
            <span className="rounded-full bg-white px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#7D8B69]" data-testid={`audit-status-${log.id}`}>{log.status_code}</span>
          </div>
          <h3 className="mt-2 font-heading text-lg font-bold text-[#1B3A1A]" data-testid={`audit-summary-${log.id}`}>{log.summary}</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            <AuditChip label="Actor" value={`${actor.label || 'Unknown'} · ${(actor.role || '').replace('_', ' ')}`} testId={`audit-actor-${log.id}`} />
            <AuditChip label="IP" value={log.ip_address || 'Unknown'} testId={`audit-ip-${log.id}`} />
            <AuditChip label="Device" value={`${log.device_type || 'Device'} · ${log.browser || 'Browser'}`} testId={`audit-device-${log.id}`} />
            <AuditChip label="Path" value={log.path || ''} testId={`audit-path-${log.id}`} />
          </div>
        </div>
        <div className="rounded-2xl bg-white px-4 py-3 font-mono text-xs font-bold uppercase tracking-wider text-[#7D8B69]" data-testid={`audit-time-${log.id}`}>{formatDateTime(log.created_at)}</div>
      </div>
      {(log.before || log.after) && <details className="mt-3 rounded-2xl bg-white p-3" data-testid={`audit-details-${log.id}`}>
        <summary className="cursor-pointer font-mono text-[10px] font-bold uppercase tracking-wider text-[#7D8B69]">Before / After</summary>
        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <AuditJsonBlock title="Before" value={log.before} testId={`audit-before-${log.id}`} />
          <AuditJsonBlock title="After" value={log.after} testId={`audit-after-${log.id}`} />
        </div>
      </details>}
    </article>
  );
}

function AuditChip({ label, value, testId }) {
  return <span className="rounded-full bg-white px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#7D8B69]" data-testid={testId}>{label}: <span className="text-[#1B3A1A]">{value}</span></span>;
}

function AuditJsonBlock({ title, value, testId }) {
  return <div data-testid={testId}><div className="mb-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#7D8B69]">{title}</div><pre className="max-h-56 overflow-auto rounded-xl bg-[#102A12] p-3 text-[10px] leading-5 text-[#F3F0E6]">{value ? JSON.stringify(value, null, 2) : 'None'}</pre></div>;
}

function formatDateTime(value) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

